import { cn } from '@/lib/utils';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger
} from '@/components/ui/hover-card';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';

import { useIsMobile } from '@/hooks/use-mobile';

import {
    SourceCallGrid,
    SourceTypeIconBox,
    formatSourceDisplayName,
    matchSection
} from './DMSourceBadgeShared';
import type { SourceSection } from './SourceSummaryContext';
import { useSourceSummary } from './SourceSummaryContext';

interface SourceBadgeProps {
    name: string;
    className?: string;
}

function getCallItemCount(call: SourceSection['calls'][number]) {
    const paramCount = call.params.filter((p) => p.key || p.value).length;
    return 1 + paramCount;
}

/**
 * Inline source citation badge rendered from [SourceName] in AI responses.
 * On hover shows a popover with structured tool-call data for that source.
 * Content is embedded directly in the element props — no network fetch needed.
 */
export function DMSourceBadge({ name, className }: SourceBadgeProps) {
    const { sources, streaming } = useSourceSummary();
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState(false);

    const section = useMemo(
        () => (sources ? matchSection(sources, name) : null),
        [sources, name]
    );
    const compactLayout = useMemo(() => {
        if (!section || section.calls.length !== 1) return false;
        return getCallItemCount(section.calls[0]) <= 2;
    }, [section]);

    const isLoading = sources === null || streaming;
    const hasMatch = section !== null;
    const contentCardWidthClass = compactLayout
        ? 'w-[min(24rem,calc(100vw-2rem))]'
        : 'w-[min(32rem,calc(100vw-2rem))]';

    const badge = (
        <Button pill
            type="button"
            variant="tertiary"
            active={isOpen}
            className={cn(
                'inline-flex items-center bg-muted/50 px-2 py-0.5 h-auto text-xs',
                'relative -top-0.5 mx-0.5 leading-5',
                !isLoading && hasMatch
                    ? 'cursor-pointer hover:bg-blue-50 hover:text-blue-950 hover:[--hover:transparent] hover:[--border:214_95%_93%] dark:hover:bg-blue-950/50 dark:hover:text-blue-100 dark:hover:[--border:214_40%_28%]'
                    : 'cursor-default',
                isOpen &&
                    'bg-blue-50 text-blue-950/80 [--active:transparent] [--border:214_95%_93%] dark:bg-blue-950/50 dark:text-blue-100 dark:[--border:214_40%_28%]',
                className
            )}
            aria-label={`Show source details for ${name}`}
        >
            {formatSourceDisplayName(name)}
        </Button>
    );

    if (isLoading || !hasMatch) return badge;

    const content = (
        <>
            {/* Header: icon + source name */}
            <div className="top-0 z-10 sticky flex items-center gap-2 bg-background px-5 pt-5 pb-4 border-border border-b w-full min-w-0">
                <SourceTypeIconBox
                    configType={section?.config_type || section?.configType}
                />
                <span className="font-medium text-foreground text-base wrap-break-word leading-6 whitespace-normal">
                    {formatSourceDisplayName(name)}
                </span>
            </div>

            {/* Parameters grid */}
            <div className="flex flex-col gap-4 px-5 pt-4 pb-4 overflow-y-auto">
                <SourceCallGrid section={section} />
            </div>
        </>
    );

    if (isMobile) {
        return (
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>{badge}</PopoverTrigger>
                <PopoverContent
                    align="start"
                    side="bottom"
                    sideOffset={8}
                    className={cn(
                        'flex flex-col bg-background p-0 border border-border rounded-xl max-h-96 overflow-hidden',
                        contentCardWidthClass
                    )}
                >
                    {content}
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <HoverCard
            openDelay={0}
            closeDelay={50}
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <HoverCardTrigger asChild>{badge}</HoverCardTrigger>
            <HoverCardContent
                align="start"
                side="bottom"
                collisionPadding={16}
                className={cn(
                    'flex flex-col bg-background p-0 border border-border rounded-xl max-h-96 overflow-hidden',
                    contentCardWidthClass
                )}
            >
                {content}
            </HoverCardContent>
        </HoverCard>
    );
}
